import React, { useEffect, useState } from 'react';
import { toast } from '../../../../../ui/toast';
import {
    getNextAssessment,
    submitAnswer,
    WellnessAnswer,
} from '../../../../../utils/api_service/wellness'; // adjust path to match where this actually lives

import {WellnessNextAssessmentResponse, WellnessQuestion,} from '../../../../../utils/types/wellness_types'

type ModalState = 'loading' | 'intro' | 'questions' | 'transition' | 'completed' | 'error';

const typeLabels: Record<string, { title: string; description: string }> = {
    DASS21: {
        title: 'DASS-21',
        description: 'A short screening covering depression, anxiety and stress over the past week.',
    },
    GAD7: {
        title: 'GAD-7',
        description: 'A short screening for generalized anxiety, over the past two weeks.',
    },
    PHQ9: {
        title: 'PHQ-9',
        description: 'A short screening for depression, over the past two weeks.',
    },
};

const optionLabels: Record<string, string[]> = {
    DASS21: [
        'Did not apply to me at all',
        'Applied to me to some degree, or some of the time',
        'Applied to me to a considerable degree, or a good part of the time',
        'Applied to me very much, or most of the time',
    ],
    GAD7: ['Not at all', 'Several days', 'More than half the days', 'Nearly every day'],
    PHQ9: ['Not at all', 'Several days', 'More than half the days', 'Nearly every day'],
};

const getTypeLabel = (type: string) =>
    typeLabels[type] ?? { title: type, description: 'A short wellness screening.' };

const getOptionLabels = (type: string) =>
    optionLabels[type] ?? ['Not at all', 'A little', 'Quite a bit', 'A lot'];

const WellnessAssessmentModal = ({
                                     inviteId,
                                     isOpen,
                                     onClose,
                                     onProgress,
                                 }: {
    inviteId: string;
    isOpen: boolean;
    onClose: () => void;
    onProgress?: () => void; // called after each successful submission, so the parent can refresh card statuses
}) => {
    const [state, setState] = useState<ModalState>('loading');
    const [current, setCurrent] = useState<WellnessNextAssessmentResponse | null>(null);
    const [nextUp, setNextUp] = useState<WellnessNextAssessmentResponse | null>(null);
    const [answers, setAnswers] = useState<Record<string, number | null>>({});
    const [submitting, setSubmitting] = useState(false);

    // fresh start every time the modal is opened
    useEffect(() => {
        if (!isOpen) return;

        setState('loading');
        setCurrent(null);
        setAnswers({});

        getNextAssessment(inviteId)
            .then((res) => {
                if (!res.ok) throw new Error();
                return res.json();
            })
            .then((data: WellnessNextAssessmentResponse) => {
                if (data.completed) {
                    setState('completed');
                    return;
                }
                setCurrent(data);
                setAnswers(
                    Object.fromEntries((data.questions ?? []).map((q) => [q.questionId, q.choiceAnswer ?? null])),
                );
                setState('intro');
            })
            .catch(() => {
                setState('error');
            });
    }, [isOpen, inviteId]);

    if (!isOpen) return null;

    const handleStart = () => setState('questions');

    const handleSelect = (questionId: string, value: number) => {
        setAnswers((prev) => ({ ...prev, [questionId]: value }));
    };

    const allAnswered = current?.questions?.every((q) => answers[q.questionId] !== null) ?? false;

    const handleSubmit = () => {
        if (!current?.attemptId || submitting) return;

        setSubmitting(true);
        const payload: WellnessAnswer[] = Object.entries(answers).map(([questionId, choiceAnswer]) => ({
            questionId,
            choiceAnswer: choiceAnswer as number,
        }));

        submitAnswer({ attemptId: current.attemptId, answers: payload })
            .then((res) => {
                if (!res.ok) throw new Error();
                return res.json();
            })
            .then((data: WellnessNextAssessmentResponse) => {
                onProgress?.();
                if (data.completed) {
                    setState('completed');
                    return;
                }
                setNextUp(data);
                setState('transition');
            })
            .catch(() => {
                toast.error('Could not submit your answers. Please try again.', { position: 'bottom-center' });
            })
            .finally(() => setSubmitting(false));
    };

    const handleContinue = () => {
        if (!nextUp) return;
        setCurrent(nextUp);
        setAnswers(
            Object.fromEntries((nextUp.questions ?? []).map((q) => [q.questionId, q.choiceAnswer ?? null])),
        );
        setNextUp(null);
        setState('intro');
    };

    const label = current ? getTypeLabel(current.type ?? '') : null;
    const options = current ? getOptionLabels(current.type ?? '') : [];

    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
            <div className='absolute inset-0 bg-slate-900/50 backdrop-blur-sm' onClick={onClose} />

            <div className='relative bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[85vh] flex flex-col overflow-hidden'>
                { /* Close button */ }
                <button
                    onClick={onClose}
                    className='absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors z-10'
                >
                    <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                    </svg>
                </button>

                { state === 'loading' && (
                    <div className='flex flex-col items-center justify-center py-24 gap-3'>
                        <div className='w-10 h-10 border-2 border-purple-200 border-t-purple-500 rounded-full animate-spin' />
                        <p className='text-slate-400 text-sm'>Loading assessment...</p>
                    </div>
                ) }

                { state === 'error' && (
                    <div className='flex flex-col items-center justify-center py-24 gap-3 px-8 text-center'>
                        <div className='w-12 h-12 bg-red-50 rounded-full flex items-center justify-center'>
                            <svg className='w-6 h-6 text-red-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z' />
                            </svg>
                        </div>
                        <p className='text-slate-700 font-semibold'>Something went wrong</p>
                        <p className='text-slate-400 text-sm'>Could not load this assessment. Please try again.</p>
                    </div>
                ) }

                { state === 'completed' && (
                    <div className='flex flex-col items-center justify-center py-24 gap-3 px-8 text-center'>
                        <div className='w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center'>
                            <svg className='w-7 h-7 text-emerald-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M4.5 12.75l6 6 9-13.5' />
                            </svg>
                        </div>
                        <p className='text-slate-700 font-semibold text-lg'>All done</p>
                        <p className='text-slate-400 text-sm'>You have completed every screening for this invite. Thank you.</p>
                        <button
                            onClick={onClose}
                            className='mt-2 px-5 py-2 rounded-xl bg-purple-500 text-white text-sm font-medium hover:bg-purple-600 transition-colors'
                        >
                            Close
                        </button>
                    </div>
                ) }

                { state === 'intro' && label && (
                    <div className='flex flex-col p-8 gap-4'>
                        <div className='w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center'>
                            <svg className='w-6 h-6 text-purple-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5}
                                      d='M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 10-7.78 7.78l1.06 1.06L12 21l7.78-7.55 1.06-1.06a5.5 5.5 0 000-7.78z'/>
                            </svg>
                        </div>

                        <div>
                            <p className='text-slate-800 font-semibold text-xl'>{ label.title }</p>
                            <p className='text-slate-500 text-sm mt-1'>{ label.description }</p>
                        </div>

                        <div className='bg-slate-50 rounded-xl p-4 flex flex-col gap-2'>
                            <p className='text-slate-700 text-sm font-medium'>Before you begin</p>
                            <ul className='text-slate-500 text-sm flex flex-col gap-1.5 list-disc pl-4'>
                                <li>There are { current?.questions?.length ?? 0 } short statements to respond to.</li>
                                <li>Answer honestly based on how you have felt recently - there are no right or wrong answers.</li>
                                <li>You will need to answer every question before you can submit.</li>
                                <li>Your responses are confidential.</li>
                            </ul>
                        </div>

                        <button
                            onClick={handleStart}
                            className='mt-2 px-5 py-2.5 rounded-xl bg-purple-500 text-white text-sm font-semibold hover:bg-purple-600 transition-colors'
                        >
                            Start Assessment
                        </button>
                    </div>
                ) }

                { state === 'questions' && current && label && (
                    <>
                        <div className='px-8 pt-8 pb-4 border-b border-slate-100'>
                            <p className='text-slate-800 font-semibold text-lg'>{ label.title }</p>
                            <p className='text-slate-400 text-xs mt-1'>
                                { Object.values(answers).filter((v) => v !== null).length } of { current.questions?.length ?? 0 } answered
                            </p>
                        </div>

                        <div className='overflow-y-auto px-8 py-6 flex flex-col gap-6'>
                            { current.questions?.map((q: WellnessQuestion, index: number) => (
                                <div key={q.questionId} className='flex flex-col gap-3'>
                                    <p className='text-slate-700 text-sm font-medium'>{ index + 1 }. { q.question }</p>
                                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-2'>
                                        { options.map((optionLabel, value) => (
                                            <button
                                                key={value}
                                                onClick={() => handleSelect(q.questionId, value)}
                                                className={`text-left text-[13px] px-3 py-2 rounded-lg border transition-colors ${
                                                    answers[q.questionId] === value
                                                        ? 'bg-purple-50 border-purple-300 text-purple-700 font-medium'
                                                        : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                                                }`}
                                            >
                                                { optionLabel }
                                            </button>
                                        )) }
                                    </div>
                                </div>
                            )) }
                        </div>

                        <div className='px-8 py-4 border-t border-slate-100 flex justify-end'>
                            <button
                                onClick={handleSubmit}
                                disabled={!allAnswered || submitting}
                                className='px-5 py-2.5 rounded-xl bg-purple-500 text-white text-sm font-semibold hover:bg-purple-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors'
                            >
                                { submitting ? 'Submitting...' : 'Submit' }
                            </button>
                        </div>
                    </>
                ) }

                { state === 'transition' && nextUp && (
                    <div className='flex flex-col items-center justify-center py-24 gap-3 px-8 text-center'>
                        <div className='w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center'>
                            <svg className='w-7 h-7 text-emerald-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M4.5 12.75l6 6 9-13.5' />
                            </svg>
                        </div>
                        <p className='text-slate-700 font-semibold text-lg'>Section complete</p>
                        <p className='text-slate-400 text-sm'>
                            Up next: { getTypeLabel(nextUp.type ?? '').title }
                        </p>
                        <button
                            onClick={handleContinue}
                            className='mt-2 px-5 py-2 rounded-xl bg-purple-500 text-white text-sm font-medium hover:bg-purple-600 transition-colors'
                        >
                            Continue
                        </button>
                    </div>
                ) }
            </div>
        </div>
    );
};

export default WellnessAssessmentModal;